import {
  CTA,
  CTAColor,
  CTAType,
  CTAVariant,
  ImageAssetData,
} from '@betterfunnel/types';
import IconButton from '@betterfunnel/ui-core/IconButton';
import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import TextField from '@betterfunnel/ui-core/TextField';
import ToggleButton from '@betterfunnel/ui-core/ToggleButton';
import {
  DialogTitle,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from '@headlessui/react';
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  CursorArrowRaysIcon,
  EnvelopeIcon,
  LinkIcon,
  PhoneIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { CSSTextArea } from '../components/FormFields/CSSTextArea';
import FileUploadInput from '../components/FormFields/FileUpload/FileUploadInput';
import QuillField from '../components/QuillField';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { classNames } from '../utils';
import { formFieldUrlValidator, isValidUrl } from '../utils/validator';

interface Props {
  item?: CTA;
  open: boolean;
  disableEditAction?: boolean;
  onClose: () => void;
  onSave: (item: CTA) => Promise<void> | void;
}

const createDefaultCTA = (disableEditAction: boolean | undefined): CTA => ({
  id: uid(8),
  title: 'Button',
  variant: 'primary',
  value: '',
  type: disableEditAction ? 'none' : 'link',
});

export default function CreateCTAButtonModal({
  item,
  open,
  disableEditAction,
  onClose,
  onSave,
}: Props) {
  const { show } = useContext(SnackbarContext);

  const [data, setData] = useState<CTA>(() =>
    createDefaultCTA(disableEditAction),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error>();
  const [rndKey, setRndKey] = useState(0);

  const { forms } = useContext(DashboardContext);

  useEffect(() => {
    setData(
      item
        ? JSON.parse(JSON.stringify(item))
        : createDefaultCTA(disableEditAction),
    );
    setError(undefined);
    setIsSaving(false);
  }, [open, item, disableEditAction]);

  const createCTA = useCallback(async () => {
    setError(undefined);

    if (!data.title) {
      setError(new Error('Button title is required'));
      show('Button title is required');
      return;
    }

    if (!data.type && !disableEditAction) {
      setError(new Error('Button action is required'));
      show('Button action is required');
      return;
    }

    if (data.type === 'link' && !data.value && !disableEditAction) {
      setError(new Error('Link url is required'));
      show('Link url is required');
      return;
    }

    if (data.type === 'link' && !isValidUrl(data.value) && !disableEditAction) {
      setError(new Error('Link url is not valid'));
      show('Link url is not valid');
      return;
    }

    if (
      data.type === 'form' &&
      (!data.value || !forms?.some((f) => f.id === data.value)) &&
      !disableEditAction
    ) {
      setError(new Error('Please select a form'));
      show('Please select a form');
      return;
    }

    if (data.type !== 'form') {
      delete data.formData;
    } else {
      data.value = data.value || data.formData?.formId || '';
      delete data.formData;
    }

    const res = onSave(data as any);
    if (res && 'then' in res) {
      setIsSaving(true);
      res.catch((e) => {
        setError(e);
        Sentry.captureException(e);
      });
      res.finally(() => setIsSaving(false));
    }
  }, [data, disableEditAction, forms, onSave, show]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isSaving,
        onClick: () => onClose(),
      },
      {
        title: item ? 'Update' : 'Create',
        type: 'primary',
        loading: isSaving,
        onClick: () => createCTA(),
      },
    ],
    [createCTA, isSaving, item, onClose],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <DialogTitle
        as="h3"
        className="text-base leading-6 font-semibold text-gray-900"
      >
        {item ? 'Update CTA button' : 'Create a new CTA button'}
      </DialogTitle>
      <div key={data.id} className="mt-4 flex flex-col gap-4">
        <QuillField
          title="Title"
          key={data.id}
          value={data.title}
          onChange={(value) => {
            setData((d) => {
              d.title = value as any;
              return { ...d };
            });
          }}
        />
        <QuillField
          title="Text Below (Optional)"
          key={rndKey + '-' + data.id}
          value={data.textBelow || ''}
          placeholder="Optional text to display below the button..."
          trailing={
            <div className="flex w-full items-center justify-end gap-2">
              <IconButton
                variant="ternary"
                size="x-small"
                onClick={() => {
                  setData((d) => {
                    d.textBelow = '';
                    return { ...d };
                  });
                  setRndKey((k) => k + 1);
                }}
              >
                <TrashIcon className="h-5 w-5" />
              </IconButton>
            </div>
          }
          onChange={(value) => {
            setData((d) => {
              d.textBelow = value as any;
              return { ...d };
            });
          }}
        />
        {!disableEditAction && (
          <>
            <Select<CTAType>
              title="Action"
              placeholder="Action"
              selected={data.type}
              onChange={(e) => {
                setData((d) => ({
                  ...d,
                  type: e,
                }));
              }}
              options={[
                {
                  value: 'link',
                  child: (
                    <span className="flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      Link
                    </span>
                  ),
                },
                {
                  value: 'phone',
                  child: (
                    <span className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4" />
                      Phone
                    </span>
                  ),
                },
                {
                  value: 'email',
                  child: (
                    <span className="flex items-center gap-2">
                      <EnvelopeIcon className="h-4 w-4" />
                      Email
                    </span>
                  ),
                },
                {
                  value: 'form',
                  child: (
                    <span className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      Form
                    </span>
                  ),
                },
                {
                  value: 'none',
                  child: (
                    <span className="flex items-center gap-2">
                      <CursorArrowRaysIcon className="h-4 w-4" />
                      None
                    </span>
                  ),
                },
              ]}
            />
            {data.type === 'link' ? (
              <TextField
                value={data.value}
                type="url"
                placeholder="https://google.com/...."
                onChange={(e) => {
                  setData((d) => {
                    d.value = e.target.value;
                    return { ...d };
                  });
                }}
                validation={formFieldUrlValidator(data.value)}
              />
            ) : (
              <></>
            )}
            {data.type === 'phone' ? (
              <TextField
                type="tel"
                value={data.value}
                placeholder="+9720501234567"
                onChange={(e) => {
                  setData((d) => {
                    d.value = e.target.value;
                    return { ...d };
                  });
                }}
              />
            ) : (
              <></>
            )}
            {data.type === 'email' ? (
              <TextField
                type="email"
                value={data.value}
                placeholder="yourmail@example.com"
                onChange={(e) => {
                  setData((d) => {
                    d.value = e.target.value;
                    return { ...d };
                  });
                }}
              />
            ) : (
              <></>
            )}
            {data.type === 'form' ? (
              <Select
                title="Lead form"
                placeholder="Select a lead form.."
                selected={(data.value || data.formData?.formId) ?? ''}
                onChange={(e: string) => {
                  const form = forms?.find((f) => f.id === e);
                  if (!form) return;
                  setData((d) => {
                    d.value = e;
                    delete d.formData;

                    return { ...d };
                  });
                }}
                loading={!forms}
                options={[
                  ...(forms?.map((f) => ({
                    value: f.id,
                    child: f.data().name,
                  })) ?? []),
                ]}
              />
            ) : (
              <></>
            )}
          </>
        )}

        <div>
          <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
            Style
          </label>
          <ToggleButton<CTAVariant>
            value={data.variant ?? 'primary'}
            items={[
              { value: 'primary', title: 'Primary' },
              { value: 'secondary', title: 'Secondary' },
              { value: 'text', title: 'Text' },
            ]}
            onChange={(variant) => {
              setData((d) => ({ ...d, variant }));
            }}
          />
        </div>
        <div>
          <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
            Color
          </label>
          <ToggleButton<CTAColor | 'default'>
            value={data.color ?? 'default'}
            items={[
              { value: 'default', title: 'Default' },
              { value: 'colored', title: 'Colored' },
              { value: 'white', title: 'White' },
            ]}
            onChange={(color) => {
              setData((d) => {
                const newData = { ...d };

                if (color === 'default') {
                  delete newData.color;
                } else {
                  newData.color = color;
                }

                return newData;
              });
            }}
          />
        </div>
        <div>
          <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
            Start Icon
          </label>
          <FileUploadInput
            image={data?.startIcon}
            types={['image']}
            onSelected={(image) => {
              setData((d) => {
                const newData = { ...d };

                if (image) {
                  newData.startIcon = image as ImageAssetData;
                } else {
                  delete newData.startIcon;
                }

                return newData;
              });
            }}
          />
        </div>
        <div>
          <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
            End Icon
          </label>
          <FileUploadInput
            image={data?.endIcon}
            types={['image']}
            onSelected={(image) => {
              setData((d) => {
                const newData = { ...d };

                if (image) {
                  newData.endIcon = image as ImageAssetData;
                } else {
                  delete newData.endIcon;
                }

                return newData;
              });
            }}
          />
        </div>
        <div>
          <Disclosure>
            {({ open }) => (
              <>
                <DisclosureButton className="flex items-center gap-1 py-2">
                  <Cog6ToothIcon className="h-4 w-4" />
                  Advanced Settings
                  <div className="flex-1" />
                  <ChevronDownIcon
                    className={classNames(
                      'h-4 w-4',
                      open ? 'rotate-180 transform' : '',
                    )}
                  />
                </DisclosureButton>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0"
                >
                  <DisclosurePanel>
                    <CSSTextArea
                      title="Custom CSS"
                      className="h-32"
                      value={data.buttonCSS}
                      onChange={(value) => {
                        setData((d) => {
                          d.buttonCSS = value;
                          return { ...d };
                        });
                      }}
                    />
                  </DisclosurePanel>
                </Transition>
              </>
            )}
          </Disclosure>
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
